import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { notificationService, Notification, NotificationType, NotificationPriority } from '../services/notificationService';

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
  deleteNotification: (notificationId: string) => void;
  clearAll: () => void;
  showToast: (message: string, type?: 'success' | 'error' | 'info' | 'warning') => void;
  toast: ToastState | null;
  closeToast: () => void;
}

interface ToastState {
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  timestamp: number;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [toast, setToast] = useState<ToastState | null>(null);

  // Load notifications on mount and when user changes
  useEffect(() => {
    if (user?.id) {
      console.log('🔔 NotificationContext: Setting up for user ID:', user.id);
      
      const loadNotifications = async () => {
        // Migrate any localStorage notifications to Firestore on first load
        console.log('🔔 NotificationContext: Attempting migration from localStorage to Firestore...');
        await notificationService.migrateLocalStorageToFirestore(user.id);
        
        // Load notifications from Firestore (or localStorage fallback)
        const userNotifications = await notificationService.getNotifications(user.id);
        const unread = await notificationService.getUnreadCount(user.id);
        console.log('🔔 NotificationContext: Loaded notifications:', userNotifications.length, 'Unread:', unread);
        setNotifications(userNotifications);
        setUnreadCount(unread);
      };

      loadNotifications();

      // Subscribe to notification changes
      const unsubscribe = notificationService.subscribe((updatedNotifications) => {
        console.log('🔔 [SUBSCRIPTION] Subscription fired with updated notifications:', updatedNotifications.length);
        // Update state directly with the updated notifications
        setNotifications(updatedNotifications);
        // Recalculate unread count from the updated notifications
        const unreadCount = updatedNotifications.filter(n => !n.read && n.dismissible !== false).length;
        setUnreadCount(unreadCount);
        console.log('🔔 [SUBSCRIPTION] Updated state - total:', updatedNotifications.length, 'Unread:', unreadCount);
      });

      return unsubscribe;
    } else {
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [user?.id]);

  const showToast = useCallback((message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    setToast({ message, type, timestamp: Date.now() });
  }, []);

  const addNotification = useCallback(async (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    if (!user?.id) return;
    await notificationService.addNotification(user.id, notification);
    
    // Optionally show toast for high priority notifications
    if (notification.priority === 'urgent' || notification.priority === 'high') {
      const toastType = notification.priority === 'urgent' ? 'warning' : 'info';
      showToast(notification.title, toastType);
    }
  }, [user?.id, showToast]);

  const markAsRead = useCallback(async (notificationId: string) => {
    if (!user?.id) return;
    try {
      await notificationService.markAsRead(user.id, notificationId);
    } catch (error) {
      console.error('❌ Context: Error marking as read:', error);
      throw error; // Re-throw for UI to handle
    }
  }, [user?.id]);

  const markAllAsRead = useCallback(async () => {
    if (!user?.id) return;
    try {
      await notificationService.markAllAsRead(user.id);
    } catch (error) {
      console.error('❌ Context: Error marking all as read:', error);
      throw error; // Re-throw for UI to handle
    }
  }, [user?.id]);

  const deleteNotification = useCallback(async (notificationId: string) => {
    if (!user?.id) return;
    try {
      await notificationService.deleteNotification(user.id, notificationId);
    } catch (error) {
      console.error('❌ Context: Error deleting notification:', error);
      throw error; // Re-throw for UI to handle
    }
  }, [user?.id]);

  const clearAll = useCallback(async () => {
    if (!user?.id) return;
    try {
      await notificationService.clearAll(user.id);
    } catch (error) {
      console.error('❌ Context: Error clearing all:', error);
      throw error; // Re-throw for UI to handle
    }
  }, [user?.id]);

  const closeToast = useCallback(() => {
    setToast(null);
  }, []);

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    addNotification,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAll,
    showToast,
    toast,
    closeToast,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  const { user } = useAuth();
  
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  
  // Make context accessible globally for debugging
  if (typeof window !== 'undefined') {
    (window as any).__notificationContext = context;
    (window as any).__currentUserId = user?.id || null;
    if (user?.id) {
      console.log('🔔 Notification context available. Current user ID:', user.id);
    }
  }
  
  return context;
};
